import React from "react";
import {Link} from "react-router-dom";
import { useLeaguePrefix } from '../../utils/leagueLinks';
import { useActiveLeague } from '../../hooks/useActiveLeague';

function PlayerItems({
    props, 
    moveItem, 
    assignedSlotsMap,
    assignedSubstitutesMap,
    currentFormationSlots,
    playerForPositionChange,
    openPositionDropdown,
    closePositionDropdown,
    handlePositionSelect
}) {

    const PositionDropdown = ({ 
        currentPlayer,
        allPlayerData,
        onSelect,
        onClose
    }) => {
        
        // Filter out the current player to create a list of players to swap with.
        const otherPlayers = allPlayerData.filter(p => p.wyId !== currentPlayer.wyId);

        return (
            <div className="sort-drop">
                <div className="sort_drop">
                    <div className="descr">Select position or player to swap with {currentPlayer.name}</div>
                    
                    <div className="sort-drop-col">
                        {/* "Remove from Teamsheet" is now the first item */}
                        <div key="remove-player"
                            className="sort_row_btn" 
                            onClick={(event) => {
                                event.stopPropagation(); 
                                onSelect(currentPlayer.wyId, { position: '-' }); // Pass remove object
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                             <div className="col r-pos-col"></div>
                             <div className="col">Remove from Teamsheet</div>
                        </div>

                        {/* Formation position slots (starting XI + substitutes) */}
                        {currentFormationSlots && currentFormationSlots.length > 0 && currentFormationSlots.map((slot) => {
                            // Skip the remove option as it's already handled above
                            if (slot.position === '-') return null;
                            
                            return (
                                <div key={slot.id || slot.position}
                                    className="sort_row_btn"
                                    onClick={(event) => {
                                        event.stopPropagation(); 
                                        onSelect(currentPlayer.wyId, { position: slot.position, id: slot.id }); // Pass position slot with id
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="col r-pos-col">{slot.position}</div>
                                    <div className="col">{slot.display || slot.position}</div>
                                </div>
                            );
                        })}

                        {/* Separator for visual clarity */}
                        {otherPlayers.length > 0 && (
                            <div className="sort_row_btn" style={{ borderTop: '1px solid #ccc', marginTop: '5px', paddingTop: '5px' }}>
                                <div className="col" style={{ fontWeight: 'bold', textAlign: 'center' }}>--- OR SWAP WITH PLAYER ---</div>
                            </div>
                        )}

                        {/* List of other players to swap with */}
                        {otherPlayers.map((player) => {
                            // Add 'active' class if the player is already placed
                            const activeClass = player.placed ? ' active' : '';
                            return (
                                <div key={player.wyId}
                                    className={`sort_row_btn${activeClass}`}
                                    onClick={(event) => {
                                        event.stopPropagation(); 
                                        onSelect(currentPlayer.wyId, player.wyId); // Pass player IDs for swap
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="col r-pos-col">{player.tacticalPosition || 'Unassigned'}</div>
                                    <div className="col">{player.name}</div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
        );
    }

    /* Extracted row component to comply with React hook rules */
    const PlayerRow = ({ player, index }) => {
        const prefix = useLeaguePrefix();
        const { activeLeague } = useActiveLeague();
        const dropdownRef = React.useRef(null);
        const isOpen = playerForPositionChange?.id === player.id;

        React.useEffect(() => {
            function handleClickOutside(event) {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    if (isOpen) {
                        closePositionDropdown();
                    }
                }
            }
            if (isOpen) {
                document.addEventListener('mousedown', handleClickOutside);
            }
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [isOpen, closePositionDropdown]);

        const activeClass = player.placed ? ' active ' : '';
        const activeDrop = isOpen ? ' active ' : '';

        return (
            <div
                className={'player-list-row' + activeClass}
            >
                <div
                    ref={dropdownRef}
                    className={'info-col m-info-col p_list_sort_btn' + activeDrop}
                    onClick={(event) => {
                        if (!event.target.closest('.pos-col')) {
                            event.stopPropagation();
                            if (isOpen) {
                                closePositionDropdown();
                            } else {
                                openPositionDropdown(player);
                            }
                        }
                    }}
                >
                    <div
                        className='info-col pos-col'
                        onClick={(e) => {
                            e.stopPropagation();
                            if (isOpen) {
                                closePositionDropdown();
                            } else {
                                openPositionDropdown(player);
                            }
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        {player.tacticalPosition || '-'}
                    </div>

                    <div className='info-col ico-col'>
                        <div className='ico icon-arrow-bottom' />
                    </div>
                    <div className='info-col name-col'>{player.name}</div>
                    <div className='info-col'>
                        <div className='rating'>
                            {player.rating}
                            {player.ratingDelta > 0 ? <span className='r-inc'>+{player.ratingDelta}</span> : null}
                            {player.ratingDelta < 0 ? <span className='r-dec'>{player.ratingDelta}</span> : null}
                        </div>
                    </div>

                    {isOpen ? (
                        <PositionDropdown
                            currentPlayer={player}
                            allPlayerData={props}
                            onSelect={handlePositionSelect}
                            onClose={closePositionDropdown}
                        />
                    ) : null}
                </div>

                <div className='info-col'>{player.primaryPosition}</div>
                <div className='info-col'>
                    {player.games}
                </div>
                <div className='info-col'>
                    {player.goals}
                </div>
                <div className='info-col'>
                    {player.assists}
                </div>
                <div className='info-col'>
                    {player.yellow}
                </div>
                <div className='info-col'>
                    {player.red}
                </div>
                <div className='link-col'>
                    <Link to={`${prefix}/player/${player.wyId}${activeLeague?._id ? `?leagueId=${activeLeague._id}` : ''}`} className='ico icon-tip' />
                </div>
            </div>
        );
    };

    /* Updated PlayerItem now maps to PlayerRow */
    const PlayerItem = (list) => {
        if (!list || list.length === 0) {
            return '';
        }

        return list.map((player, index) => (
            <PlayerRow
                key={player.id || index}
                player={player}
                index={index}
            />
        ));
    };

    const CountAlert = () => {

        return (
            <div className="count-alert">
                <div className="tip">
                    Your squad must have a minimum of 22 players
                </div>

                <Link to={`/transfers`} className="settings-submit-btn">
                    TO TRANSFERS
                </Link>

            </div>
        )
    }

    return (
        <div className="players-list-container">
            {PlayerItem(props)}
            { props.length < 22 ? CountAlert() : ''}
        </div>
    )
}

export {PlayerItems}
